import { Button } from "../ui/button";
import { useTranslation } from "../../i18n";

/** Run states in which the campaign is still sending. */
const ACTIVE_STATES = new Set(["running", "stopping"]);

/**
 * Live progress for a campaign run, with the stop control.
 *
 * The stop button is the important part: a broadcast can be tens of thousands
 * of recipients, and without it the only way to halt a run that is already
 * sending was to restart the service.
 *
 * "Stopping" is a distinct state from "Stopped" — the request is durable and
 * asynchronous, so the run may still be finishing the recipient it is on.
 */
export default function CampaignRunProgress({ run, onStop }) {
  const { t } = useTranslation();

  if (!run) return null;

  const active = ACTIVE_STATES.has(run.runState);
  const total = run.runCandidates || 0;
  const sent = run.runSent || 0;
  const failed = run.runFailed || 0;
  const percent =
    total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0;

  if (!active && run.runState === "idle") return null;

  return (
    <div className="w-full shrink-0">
      <div className="bg-c-gray-600 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl">{t("campaign.runProgress")}</h2>
            <p className="text-c-gray-400 text-sm" aria-live="polite">
              {t(`campaign.runState.${run.runState}`, {
                sent,
                total,
                failed,
                by: run.runTriggeredBy || "—",
              })}
            </p>
          </div>
          {active && (
            <Button
              variant="destructive"
              onClick={() => onStop(run.campaignId)}
              disabled={run.runState === "stopping"}
            >
              {run.runState === "stopping"
                ? t("campaign.stopping")
                : t("campaign.stop")}
            </Button>
          )}
        </div>

        {total > 0 && (
          <div
            className="h-2 w-full rounded-full bg-c-gray-500 overflow-hidden"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={`h-full ${active ? "bg-accent" : "bg-green-500"}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        )}

        {run.runError && (
          <p className="text-red-400 text-sm">
            {t("campaign.runError", { error: run.runError })}
          </p>
        )}

        {failed > 0 && (
          <p className="text-c-gray-400 text-sm">
            {t("campaign.runFailedHint")}
          </p>
        )}
      </div>
    </div>
  );
}
